import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { signOut } from 'firebase/auth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { useUserSession } from '@/context/user-session-context';
import { useFirebase } from '@/firebase';

export const Route = createFileRoute('/account-pending-deletion')({
  component: AccountPendingDeletionPage,
});

function AccountPendingDeletionPage() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useUserSession();
  const { auth, backend } = useFirebase();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<'sent' | 'error' | null>(null);
  const [showRestore, setShowRestore] = useState(false);
  const [restoreLink, setRestoreLink] = useState('');

  const resend = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await backend.billing.resendAccountDeletionEmail();
      setMessage('sent');
    } catch {
      setMessage('error');
    } finally {
      setBusy(false);
    }
  };

  const openRestoreLink = () => {
    try {
      const value = restoreLink.trim();
      const token = value.includes('://')
        ? new URL(value).searchParams.get('token')
        : value;
      if (!token) throw new Error('Missing token');
      window.location.assign(`/restore-account?token=${encodeURIComponent(token)}`);
    } catch {
      setMessage('error');
    }
  };

  const rawScheduledFor = user?.deletionScheduledFor;
  const scheduledDate = rawScheduledFor ? new Date(rawScheduledFor) : null;
  const scheduledFor = scheduledDate && !Number.isNaN(scheduledDate.getTime())
    ? scheduledDate.toLocaleString(i18n.language, {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: 'Europe/Madrid',
    })
    : t('accountDeletion.pendingUnknownDate');

  return (
    <AuthGuard>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 4, md: 10 } }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: { xs: 3, md: 5 } }}>
            <Typography variant="h4" gutterBottom>{t('accountDeletion.pendingTitle')}</Typography>
            {loading ? <CircularProgress /> : (
              <Stack spacing={3}>
                <Alert severity="warning">{t('accountDeletion.pendingPublications')}</Alert>
                <Typography>{t('accountDeletion.pendingScheduledFor', { date: scheduledFor })}</Typography>
                {message === 'sent' && <Alert severity="success">{t('accountDeletion.resendSuccess')}</Alert>}
                {message === 'error' && <Alert severity="error">{t('accountDeletion.resendError')}</Alert>}
                <Button variant="contained" disabled={busy} onClick={resend}>
                  {busy ? <CircularProgress size={20} color="inherit" /> : t('accountDeletion.resend')}
                </Button>
                <Button variant="outlined" onClick={() => setShowRestore((value) => !value)}>
                  {t('accountDeletion.haveRestoreLink')}
                </Button>
                {showRestore && (
                  <Stack spacing={2}>
                    <TextField
                      value={restoreLink}
                      onChange={(event) => setRestoreLink(event.target.value)}
                      label={t('accountDeletion.restoreLinkLabel')}
                      fullWidth
                    />
                    <Button onClick={openRestoreLink} disabled={!restoreLink.trim()}>
                      {t('accountDeletion.restoreTitle')}
                    </Button>
                  </Stack>
                )}
                <Button color="inherit" onClick={() => signOut(auth)}>
                  {t('accountDeletion.signOut')}
                </Button>
              </Stack>
            )}
          </Paper>
        </Container>
      </Box>
    </AuthGuard>
  );
}
